import { useEffect, useState } from "react";
import instance from "../../services/endpoint";
import {
  deleteUser,
  reactivateUser,
  freezeUser,
  unfreezeUser,
} from "../../services/apiService";
import { BiSolidUserCheck, BiSolidUserX } from "react-icons/bi";
import { TbFileDownload } from "react-icons/tb";
import { MdVerifiedUser, MdDoNotDisturbOn, MdDelete } from "react-icons/md";
import { FaUserCheck } from "react-icons/fa";
import { FaSnowflake } from "react-icons/fa6";
import { Row, Spinner, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { Pagination } from "react-bootstrap"; // Import Bootstrap Pagination component
import Loader from "../utilComponents/loader";
import ShimmerLoader from "../utilComponents/shimmer";
import {
  useAllUsers,
  useAllAdmins,
} from "../../services/hooks/getSuperAdminApis";

const AllUsers = ({ searchQuery }) => {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1); // Track current page
  const itemsPerPage = 5; // Items per page
  const [assignLoading, setAssignLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [adminInput, setAdminInput] = useState("");
  const [depositAmount, setDepositAmount] = useState("");
  const [withdrawPeriod, setWithdrawPeriod] = useState("1"); // Default to Yearly
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showAccTypeModal, setShowAccTypeModal] = useState(false);
  const [accType, setAccType] = useState("");
  const [freezeLoading, setFreezeLoading] = useState(false);
  const [allAdminsList, setAllAdminsList] = useState([]);
  const [focusedAssignUserId, setFocusedAssignUserId] = useState(null);

  // Fetch all users using custom hook
  const { data: allUsers, isLoading } = useAllUsers();
  // fetch all admins using custom hook
  const { data: allAdmins } = useAllAdmins();

  useEffect(() => {
    if (Array.isArray(allAdmins)) {
      setAllAdminsList(allAdmins.map((admin) => admin.AdminID));
    }
  }, [allAdmins]);

  useEffect(() => {
    if (allUsers && Array.isArray(allUsers)) {
      setUsers(allUsers.reverse());
    }
  }, [allUsers]);

  //  **Filter requests based on searchQuery**
  const filteredRequests = users.filter((request) => {
    const searchLower = searchQuery.toLowerCase();
    const isVerified = request.isEmailVerified ? "verified" : "not checked";

    return (
      request.AccountID.toLowerCase().includes(searchLower) ||
      request.Email.toLowerCase().includes(searchLower) ||
      request.FullName.toLowerCase().includes(searchLower) ||
      isVerified.includes(searchLower) ||
      (searchLower === "verified" && request.isEmailVerified) ||
      (searchLower === "not checked" && !request.isEmailVerified)
    );
  });

  // Calculate total pages for users`
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredRequests.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // download CSV function
  const downloadCSV = () => {
  const headers = [
    "JoinedDate",
    "AccountID",
    "AdminID",
    "FullName",
    "Email",
    "EmailVerified",
    "Phone",
    "Account Type",
    "Address",
    "ReferralID",
    "Encrypted Amount",
    "TotalInvestment",
    "CurrentInvestment",
    "TotalReturns",
    "CurrentReturns",
    "Role",
    "KYC-Status",
    "User Status",
  ];

  const escapeCSV = (value) => {
    if (value === null || value === undefined) return '""';
    const str = value.toString();
    return `"${str.replace(/"/g, '""')}"`; // Escape quotes and wrap
  };

  const rows = filteredRequests.map((user) => [
    escapeCSV(user.createdAt ? new Date(user.createdAt).toLocaleDateString() : ""),
    escapeCSV(user.AccountID),
    escapeCSV(user.AdminID || ""),
    escapeCSV(user.FullName),
    escapeCSV(user.Email),
    escapeCSV(user.isEmailVerified ? "Verified" : "Not Checked"),
    escapeCSV(user.Phone),
    escapeCSV(user.Account_Type),
    escapeCSV(user.Address || ""),
    escapeCSV(user.ReferralID || ""),
    escapeCSV(user.amount !== undefined ? user.amount : ""),
    escapeCSV(user.totalInvestment !== undefined ? user.totalInvestment : ""),
    escapeCSV(user.currentInvestment !== undefined ? user.currentInvestment : ""),
    escapeCSV(user.totalReturns !== undefined ? user.totalReturns : ""),
    escapeCSV(user.currentReturns !== undefined ? user.currentReturns : ""),
    escapeCSV(user.Role),
    escapeCSV(user.KYC_Status),
    escapeCSV(user.isDeleted ? "Deleted" : "Active"),
  ]);

  const csvContent = [headers.map(escapeCSV), ...rows]
    .map((row) => row.join(","))
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", "All-Users.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  // ====================================API FUNCTIONS===================================================
  // Function to assign admin to a user=============================
  const handleAssignAdmin = async (userAccID, adminID) => {
    if (!adminID) {
      toast.error("Please enter a valid Admin ID");
      return;
    }
    try {
      setAssignLoading(true);
      const response = await instance.put(
        "/api/v1/superadmin/auth/assignadmintouser",
        { userAccID, adminID }
      );
      if (response.status === 200) {
        toast.success("Admin assigned successfully");
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.AccountID === userAccID ? { ...user, AdminID: adminID } : user
          )
        );
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to assign admin");
    } finally {
      setAssignLoading(false);
    }
  };

  // Function to deposit amount on behalf of a user====================
  const handleDepositAmount = async (
    AccountID,
    AccountType,
    amount,
    withdraw_period
  ) => {
    if (!amount || !withdraw_period) {
      toast.error("Please enter a valid amount and withdraw period");
      return;
    }
    try {
      setAssignLoading(true);
      const response = await instance.post(
        "/api/v1/deposit/adminSubmitDepositReq",
        { AccountID, AccountType, amount, withdraw_period }
      );
      if (response.status === 200) {
        toast.success("Deposit request submitted successfully");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to submit deposit request"
      );
    } finally {
      setAssignLoading(false);
    }
  };

  // Function to change Account Type====================
  const handleChangeAccType = async (AccountID, AccountType) => {
    if (!AccountType) {
      toast.error("Please enter a valid Account Type");
      return;
    }
    try {
      setAssignLoading(true);
      const response = await instance.put(
        `/api/v1/admin/control/changeAccountType/${AccountID}`,
        { Account_Type: AccountType }
      );
      if (response.status === 200) {
        toast.success("Account Type changed successfully");
      }
      // Update the user in the state
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.AccountID === AccountID
            ? { ...user, Account_Type: AccountType }
            : user
        )
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to change Account Type"
      );
    } finally {
      setAssignLoading(false);
    }
  };

  return (
    <>
      {freezeLoading && <Loader />}
      {deleteLoading && <Loader />}
      {isLoading && <Loader />}
      <div>
        <h2>Users Management</h2>
        <div className="csv">
          <p style={{ color: "green" }}>
            Total Users: {filteredRequests.length}
          </p>
          <button
            onClick={downloadCSV}
            className="downloadBtn"
            style={{ alignSelf: "flex-end" }}>
            <TbFileDownload />
          </button>
        </div>
        <div className="scrollable-table">
          <table>
            <thead style={{ fontSize: "0.8rem" }}>
              <tr>
                <th>JoinedDate</th>
                <th>Account ID</th>
                <th>Admin ID</th>
                <th>FullName</th>
                <th>Email</th>
                <th>EmailVerified</th>
                <th>Phone</th>
                <th>Account Type</th>
                <th>Address</th>
                <th>ReferralID</th>
                <th>Encrypted Amount</th>
                <th>TotalInvestment</th>
                <th>TotalReturns</th>
                <th>CurrentInvestment</th>
                <th>CurrentReturns</th>
                <th>Role</th>
                <th>KYC-Status</th>
                <th>Account Freez</th>
                <th>User Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(currentItems) && currentItems.length > 0 ? (
                currentItems.map((user) => (
                  <tr key={user.AccountID}>
                    <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                    <td>{user.AccountID}</td>
                    {/* assign admin / adminID */}
                    <td
                      style={{
                        position: "relative",
                        alignItems: "center",
                        width: "10rem",
                      }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: "12rem",
                        }}>
                        {user.AdminID === null ? (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              textAlign: "center",
                              width: "8rem",
                            }}>
                            <small style={{ color: "red", fontSize: "0.8rem" }}>
                              Assign Admin
                            </small>
                            <input
                              type="text"
                              placeholder="Enter Admin ID"
                              style={{
                                margin: "4px 0",
                                width: "150%",
                                height: "25px",
                                fontSize: "0.8rem",
                                textAlign: "center",
                              }}
                              value={user._assignAdminInput || ""}
                              onChange={(e) => {
                                setUsers((prev) =>
                                  prev.map((u) =>
                                    u.AccountID === user.AccountID
                                      ? {
                                          ...u,
                                          _assignAdminInput: e.target.value,
                                        }
                                      : u
                                  )
                                );
                              }}
                              onFocus={() =>
                                setFocusedAssignUserId(user.AccountID)
                              }
                              // onBlur={() => {
                              //   // Optionally delay hiding dropdown so click still works
                              //   setTimeout(
                              //     () => setFocusedAssignUserId(null),
                              //     200
                              //   );
                              // }}
                            />
                            {focusedAssignUserId === user.AccountID && (
                              <div
                                style={{
                                  marginTop: "3rem",
                                  maxHeight: "100px",
                                  overflowY: "auto",
                                  width: "150%!important",
                                  border: "1px solid #ccc",
                                  backgroundColor: "#444",
                                  color: "#fff",
                                  position: "absolute",
                                  zIndex: 1000,
                                }}>
                                {allAdminsList.map((adminId) => (
                                  <div
                                    key={adminId}
                                    style={{
                                      padding: "4px",
                                      cursor: "pointer",
                                      fontSize: "0.8rem",
                                    }}
                                    onClick={() => {
                                      // Set selected adminId in input
                                      setUsers((prev) =>
                                        prev.map((u) =>
                                          u.AccountID === user.AccountID
                                            ? {
                                                ...u,
                                                _assignAdminInput: adminId,
                                              }
                                            : u
                                        )
                                      );
                                      setFocusedAssignUserId(null);
                                    }}>
                                    {adminId}
                                  </div>
                                ))}
                              </div>
                            )}

                            <button
                              style={{
                                fontSize: "0.8rem",
                                padding: "2px 8px",
                                backgroundColor: "green",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                              }}
                              onClick={() => {
                                const adminID = user._assignAdminInput;
                                // Call the API to assign admin
                                handleAssignAdmin(user.AccountID, adminID);
                              }}>
                              {assignLoading ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                "Assign"
                              )}
                            </button>
                          </div>
                        ) : (
                          user.AdminID
                        )}
                      </div>
                    </td>
                    <td>{user.FullName}</td>
                    <td>{user.Email}</td>
                    <td>
                      {user.isEmailVerified ? (
                        <MdVerifiedUser style={{ color: "green" }} />
                      ) : (
                        <MdDoNotDisturbOn style={{ color: "red" }} />
                      )}
                    </td>
                    <td>{user.Phone}</td>
                    <td>{user.Account_Type}</td>
                    <td
                      style={{
                        minWidth: "220px",
                        maxWidth: "350px",
                        wordBreak: "break-word",
                        whiteSpace: "pre-line",
                      }}>
                      {user.Address ? user.Address : "N/A"}
                    </td>
                    <td>{user.ReferralID}</td>
                    <td>₹{user.amount}</td>
                    <td>₹{user.totalInvestment}</td>
                    <td>₹{user.totalReturns}</td>
                    <td style={{ color: "green" }}>
                      ₹{user.currentInvestment}
                    </td>
                    <td style={{ color: "green" }}>₹{user.currentReturns}</td>
                    <td>{user.Role}</td>
                    <td>
                      <span
                        style={{
                          color:
                            user.KYC_Status === "Pending" ? "red" : "green",
                        }}>
                        {user.KYC_Status}
                      </span>
                    </td>
                    <td>
                      {user.isFreezed ? (
                        <span style={{ color: "blue" }}>
                          <FaSnowflake /> Freezed
                        </span>
                      ) : (
                        <span style={{ color: "green" }}>
                          <BiSolidUserCheck /> Active
                        </span>
                      )}
                    </td>
                    <td>
                      {user.isDeleted ? (
                        <span style={{ color: "red" }}>
                          <BiSolidUserX /> Deleted
                        </span>
                      ) : (
                        <span style={{ color: "green" }}>
                          <BiSolidUserCheck /> Active
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        width: "200px",
                        minWidth: "200px",
                        maxWidth: "200px",
                      }}>
                      <div
                        className="d-flex justify-content-space-between align-items-space-between"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: "100%",
                        }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "0.5rem",
                            flexWrap: "wrap",
                            // marginRight: "0.5rem",
                            marginBottom: "0.5rem",
                          }}>
                          {user.isDeleted ? (
                            <button
                              style={{
                                background: "green",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                padding: "2px 8px",
                                fontSize: "0.8rem",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setSelectedUser(user); // sets the full user object
                                setShowReactivateModal(true); // show modal
                              }}>
                              Reactivate
                            </button>
                          ) : (
                            <button
                              style={{
                                background: "black",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                padding: "2px 8px",
                                fontSize: "0.8rem",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setSelectedUser(user); // sets the full user object
                                setShowDeleteModal(true); // show modal
                              }}>
                              Delete
                            </button>
                          )}
                          <button
                            style={{
                              background: "goldenrod",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              padding: "2px 8px",
                              fontSize: "0.7rem",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setSelectedUser(user);
                              setShowModal(true);
                            }}>
                            Change Admin
                          </button>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "0.5rem",
                            flexWrap: "wrap",
                          }}>
                          <button
                            style={{
                              background: "blueviolet",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              padding: "2px 8px",
                              fontSize: "0.8rem",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setSelectedUser(user); // sets the full user object
                              setShowDepositModal(true); // show modal
                            }}>
                            Deposit
                          </button>
                          <button
                            style={{
                              background: "purple",
                              color: "#fff",
                              border: "none",
                              borderRadius: "4px",
                              padding: "2px 8px",
                              fontSize: "0.7rem",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setSelectedUser(user);
                              setShowAccTypeModal(true);
                            }}>
                            Account Type
                          </button>
                          {user.isFreezed ? (
                            <button
                              style={{
                                background: "green",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                padding: "2px 8px",
                                fontSize: "0.7rem",
                                cursor: "pointer",
                              }}
                              onClick={async () => {
                                setFreezeLoading(true);
                                await unfreezeUser(user.AccountID);
                                setUsers((prevUsers) =>
                                  prevUsers.map((u) =>
                                    u.AccountID === user.AccountID
                                      ? { ...u, isFreezed: false }
                                      : u
                                  )
                                );
                                toast.success("User unfreezed successfully");
                                setFreezeLoading(false);
                                setSelectedUser(null);
                              }}>
                              Un Freeze
                            </button>
                          ) : (
                            <button
                              style={{
                                background: "blue",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                padding: "2px 8px",
                                fontSize: "0.7rem",
                                cursor: "pointer",
                              }}
                              onClick={async () => {
                                setFreezeLoading(true);
                                await freezeUser(user.AccountID);
                                setUsers((prevUsers) =>
                                  prevUsers.map((u) =>
                                    u.AccountID === user.AccountID
                                      ? { ...u, isFreezed: true }
                                      : u
                                  )
                                );
                                toast.success("User freezed successfully");
                                setFreezeLoading(false);
                                setSelectedUser(null);
                              }}>
                              Freeze
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : isLoading ? (
                <tr>
                  <td colSpan="20">
                    <ShimmerLoader width="100%" height="30px" />
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan="20">No users found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            {[...Array(totalPages)].map((_, pageIndex) => (
              <Pagination.Item
                key={pageIndex}
                active={currentPage === pageIndex + 1}
                onClick={() => handlePageChange(pageIndex + 1)}>
                {pageIndex + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </div>
      </div>

      {/*===================================================Modals==================================================== */}
      {/* Modal for change Account Type */}
      {showAccTypeModal && selectedUser && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}>
          <div
            className="modal-content"
            style={{
              backgroundColor: "#fff",
              padding: "2rem",
              borderRadius: "8px",
              width: "400px",
              maxWidth: "90%",
              boxShadow: "0 0 15px rgba(0,0,0,0.2)",
              position: "relative",
            }}>
            <h4>Change Account Type for {selectedUser.FullName}</h4>
            <p>
              <strong>Account ID:</strong> {selectedUser.AccountID}
            </p>
            <p>
              <strong>Account_Type:</strong>{" "}
              {selectedUser.Account_Type || "None"}
            </p>

            <Form.Group className="mb-2" controlId="formAccountType">
              <Form.Label
                style={{
                  fontSize: "0.8rem",
                  margin: "0 0 1px -15rem",
                  color: "white",
                }}>
                Account Type
              </Form.Label>
              <Form.Select
                name="Account_Type"
                value={accType}
                onChange={(e) => setAccType(e.target.value)}
                required>
                <option value="">Select Account Type</option>
                <option value="Classic">Classic</option>
                <option value="Premium">Premium</option>
                <option value="Business">Business</option>
                <option value="Enterprise">Enterprise</option>
              </Form.Select>
            </Form.Group>

            {/* Deposit Button */}
            <button
              style={{
                padding: "8px 12px",
                backgroundColor: "#27ae60",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={async () => {
                await handleChangeAccType(selectedUser.AccountID, accType);
                setShowAccTypeModal(false);
                setAccType("");
                setSelectedUser(null);
              }}>
              {assignLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Change Account Type"
              )}
            </button>

            <button
              onClick={() => setShowAccTypeModal(false)}
              style={{
                backgroundColor: "red",
                color: "white",
                position: "absolute",
                top: "10px",
                right: "10px",
                border: "none",
                fontSize: "1.2rem",
                cursor: "pointer",
                borderRadius: "50%",
                width: "30px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}>
              x
            </button>
          </div>
        </div>
      )}

      {/* Modal for depositing amount */}
      {showDepositModal && selectedUser && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}>
          <div
            className="modal-content"
            style={{
              backgroundColor: "#fff",
              padding: "2rem",
              borderRadius: "8px",
              width: "400px",
              maxWidth: "90%",
              boxShadow: "0 0 15px rgba(0,0,0,0.2)",
              position: "relative",
            }}>
            <h4>Deposit Amount on behalf of {selectedUser.FullName}</h4>
            <p>
              <strong>Account ID:</strong> {selectedUser.AccountID}
            </p>
            <p>
              <strong>Account_Type:</strong>{" "}
              {selectedUser.Account_Type || "None"}
            </p>

            <hr />
            {/* amount input */}
            <label className="form-label text-start mb-0">Deposit Amount</label>
            <input
              type="text"
              placeholder="Enter Amount"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                margin: "10px 0",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
            {/* select withdraw_period */}
            <Row className="mb-3">
              <Form.Group className="text-start">
                <Form.Label className="text-start mb-2">Withdraw Period (optional)</Form.Label>
                <Form.Select
                  name="withdrawPeriod"
                  value={withdrawPeriod}
                  onChange={(e) => setWithdrawPeriod(e.target.value)}
                  required>
                  {/* <option value="">Select Withdraw Period</option> */}
                  <option value="1" selected>Yearly</option>
                  <option value="2">Half Yearly</option>
                  <option value="4">Quarterly</option>
                </Form.Select>
              </Form.Group>
            </Row>

            {/* Deposit Button */}
            <button
              style={{
                padding: "8px 12px",
                backgroundColor: "#27ae60",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={async () => {
                await handleDepositAmount(
                  selectedUser.AccountID,
                  selectedUser.Account_Type,
                  depositAmount,
                  withdrawPeriod,
                );
                setShowDepositModal(false);
                setDepositAmount("");
                setWithdrawPeriod("");
              }}>
              {assignLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Deposit"
              )}
            </button>

            <button
              onClick={() => setShowDepositModal(false)}
              style={{
                backgroundColor: "red",
                color: "white",
                position: "absolute",
                top: "10px",
                right: "10px",
                border: "none",
                fontSize: "1.2rem",
                cursor: "pointer",
                borderRadius: "50%",
                width: "30px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}>
              x
            </button>
          </div>
        </div>
      )}

      {/* Modal for assigning admin */}
      {showModal && selectedUser && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}>
          <div
            className="modal-content"
            style={{
              backgroundColor: "#fff",
              padding: "2rem",
              borderRadius: "8px",
              width: "400px",
              maxWidth: "90%",
              boxShadow: "0 0 15px rgba(0,0,0,0.2)",
              position: "relative",
            }}>
            <h4>Change Admin for this User</h4>
            <p>
              <strong>Name:</strong> {selectedUser.FullName}
            </p>
            <p>
              <strong>Account ID:</strong> {selectedUser.AccountID}
            </p>
            <p>
              <strong>Old Admin:</strong> {selectedUser.AdminID || "None"}
            </p>

            <input
              type="text"
              placeholder="Enter new Admin ID"
              value={adminInput}
              onChange={(e) => setAdminInput(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                margin: "10px 0",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />

            <button
              style={{
                padding: "8px 12px",
                backgroundColor: "#27ae60",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={async () => {
                await handleAssignAdmin(selectedUser.AccountID, adminInput);
                setShowModal(false);
                setAdminInput("");
              }}>
              {assignLoading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Assign"
              )}
            </button>

            <button
              onClick={() => setShowModal(false)}
              style={{
                backgroundColor: "red",
                color: "white",
                position: "absolute",
                top: "10px",
                right: "10px",
                border: "none",
                fontSize: "1.2rem",
                cursor: "pointer",
                borderRadius: "50%",
                width: "30px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}>
              x
            </button>
          </div>
        </div>
      )}

      {/* delete confirmation modal */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}>
          <div
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "8px",
              textAlign: "center",
              minWidth: "300px",
            }}>
            <MdDelete style={{ color: "red", fontSize: "5rem" }} />
            <h5>Are you sure you want to delete this user?</h5>
            <div
              style={{
                marginTop: "1rem",
                display: "flex",
                justifyContent: "space-around",
              }}>
              <button
                style={{
                  backgroundColor: "red",
                  color: "white",
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: "4px",
                }}
                onClick={async () => {
                  if (selectedUser && selectedUser.AccountID) {
                    try {
                      setDeleteLoading(true);
                      await deleteUser(selectedUser.AccountID);
                      toast.success("User deleted successfully");

                      // Optionally refresh the users list
                      setUsers((prev) =>
                        prev.map((u) =>
                          u.AccountID === selectedUser.AccountID
                            ? { ...u, isDeleted: true }
                            : u
                        )
                      );
                    } catch (error) {
                      toast.error(
                        error?.response?.data?.message ||
                          "Failed to delete user"
                      );
                      console.error("Delete error:", error);
                    } finally {
                      setDeleteLoading(false);
                    }
                  }
                  setShowDeleteModal(false);
                  setSelectedUser(null);
                }}>
                Yes
              </button>

              <button
                style={{
                  backgroundColor: "gray",
                  color: "white",
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: "4px",
                }}
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedUser(null); // reset
                }}>
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate confirmation modal */}
      {showReactivateModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}>
          <div
            style={{
              backgroundColor: "#fff",
              padding: "20px",
              borderRadius: "8px",
              textAlign: "center",
              minWidth: "300px",
            }}>
            <FaUserCheck style={{ color: "green", fontSize: "5rem" }} />
            <h5>Are you sure you want to reactivate this user?</h5>
            <div
              style={{
                marginTop: "1rem",
                display: "flex",
                justifyContent: "space-around",
              }}>
              <button
                style={{
                  backgroundColor: "green",
                  color: "white",
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: "4px",
                }}
                onClick={async () => {
                  if (selectedUser && selectedUser.AccountID) {
                    try {
                      setDeleteLoading(true);
                      await reactivateUser(selectedUser.AccountID);
                      toast.success("User reactivated successfully");

                      // Optionally refresh the users list
                      setUsers((prev) =>
                        prev.map((u) =>
                          u.AccountID === selectedUser.AccountID
                            ? { ...u, isDeleted: false }
                            : u
                        )
                      );
                    } catch (error) {
                      toast.error(
                        error?.response?.data?.message ||
                          "Failed to reactivate user"
                      );
                      console.error("Reactivate error:", error);
                    } finally {
                      setDeleteLoading(false);
                    }
                  }
                  setShowReactivateModal(false);
                  setSelectedUser(null);
                }}>
                Yes
              </button>

              <button
                style={{
                  backgroundColor: "gray",
                  color: "white",
                  padding: "6px 12px",
                  border: "none",
                  borderRadius: "4px",
                }}
                onClick={() => {
                  setShowReactivateModal(false);
                  setSelectedUser(null); // reset
                }}>
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AllUsers;
